<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  banco: { type: [String, Number], default: '' },
  catalog: { type: Object, default: () => ({}) },
  checkEngenharia: { type: Boolean, default: false },
  dataEngenharia: { type: String, default: '' },
  errors: { type: Object, default: () => ({}) },
  horaEngenharia: { type: String, default: '' },
});

const emit = defineEmits([
  'update:banco',
  'update:checkEngenharia',
  'update:dataEngenharia',
  'update:horaEngenharia',
]);

const { t } = useI18n();
const bankOptions = computed(() => props.catalog.banks || []);

const normalizeDate = value =>
  value
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2}\/\d{2})(\d)/, '$1/$2');
const normalizeTime = value =>
  value
    .replace(/\D/g, '')
    .slice(0, 4)
    .replace(/(\d{2})(\d{2})$/, '$1:$2');

const errorLabel = code => {
  if (code === 'REQUIRED') return t('POST_SALE.FORM.VALIDATION.REQUIRED');
  if (code === 'INVALID_DATE') {
    return t('POST_SALE.FORM.VALIDATION.INVALID_DATE');
  }
  if (code === 'INVALID_TIME') {
    return t('POST_SALE.FORM.VALIDATION.INVALID_TIME');
  }

  return '';
};
</script>

<template>
  <section class="grid gap-4">
    <h2 class="text-lg font-semibold text-lais-text">
      {{ t('POST_SALE.FORM.ENGINEERING.TITLE') }}
    </h2>
    <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
      {{ t('POST_SALE.FORM.BANK') }}
      <select
        :value="banco"
        :aria-invalid="Boolean(errors.banco)"
        class="min-h-10 rounded-lais border bg-white px-2 font-normal"
        :class="errors.banco ? 'border-red-500' : 'border-lais-border'"
        @change="emit('update:banco', $event.target.value)"
      >
        <option value="">{{ t('POST_SALE.FORM.SELECT') }}</option>
        <option v-for="bank in bankOptions" :key="bank.id" :value="bank.id">
          {{ bank.name }}
        </option>
      </select>
      <span v-if="errors.banco" class="text-xs font-normal text-red-600">
        {{ errorLabel(errors.banco) }}
      </span>
    </label>
    <div class="grid gap-4 sm:grid-cols-2">
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.DAY') }}
        <input
          :value="dataEngenharia"
          :aria-invalid="Boolean(errors.data_engenharia)"
          inputmode="numeric"
          maxlength="10"
          :placeholder="t('POST_SALE.FORM.ENGINEERING.DATE_PLACEHOLDER')"
          class="min-h-10 rounded-lais border px-2 font-normal"
          :class="
            errors.data_engenharia ? 'border-red-500' : 'border-lais-border'
          "
          @input="
            emit('update:dataEngenharia', normalizeDate($event.target.value))
          "
        />
        <span
          v-if="errors.data_engenharia"
          class="text-xs font-normal text-red-600"
        >
          {{ errorLabel(errors.data_engenharia) }}
        </span>
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.FORM.TIME') }}
        <input
          :value="horaEngenharia"
          :aria-invalid="Boolean(errors.hora_engenharia)"
          inputmode="numeric"
          maxlength="5"
          :placeholder="t('POST_SALE.FORM.ENGINEERING.TIME_PLACEHOLDER')"
          class="min-h-10 rounded-lais border px-2 font-normal"
          :class="
            errors.hora_engenharia ? 'border-red-500' : 'border-lais-border'
          "
          @input="
            emit('update:horaEngenharia', normalizeTime($event.target.value))
          "
        />
        <span
          v-if="errors.hora_engenharia"
          class="text-xs font-normal text-red-600"
        >
          {{ errorLabel(errors.hora_engenharia) }}
        </span>
      </label>
    </div>
    <label class="flex items-center gap-2 text-sm text-lais-text-body">
      <input
        :checked="checkEngenharia"
        type="checkbox"
        @change="emit('update:checkEngenharia', $event.target.checked)"
      />
      <span>{{ t('POST_SALE.FORM.ENGINEERING.APPROVED') }}</span>
    </label>
  </section>
</template>
